import Phaser from 'phaser';
import type { SaveManager } from '@/game/systems/SaveManager';
import { FOODS, foodTextureKey } from '@/data/foods';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, bodyTextStyle, createButton, titleTextStyle } from '@/game/ui/theme';

const SWATCH_SIZE = 132;

/** Lets the player choose which treats can appear in gameplay. Unlike Cat/Background
 * select, there's no unlock economy here -- every treat is always available, this is
 * purely a checklist -- but the "at least one enabled" rule is enforced (see
 * SaveManager.setFoodEnabled), so a tap that would empty the list is a no-op. */
export class TreatSelectScene extends Phaser.Scene {
  private saveManager!: SaveManager;

  constructor() {
    super('TreatSelectScene');
  }

  create(): void {
    this.saveManager = this.game.registry.get('saveManager');

    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, PALETTE.bgBottom).setOrigin(0);
    this.add.text(INTERNAL_WIDTH / 2, 60, 'Choose Your Treats', titleTextStyle(32)).setOrigin(0.5);
    this.add
      .text(INTERNAL_WIDTH / 2, 104, 'Tap a treat to turn it on or off', bodyTextStyle(18, '#cbb9e8'))
      .setOrigin(0.5);

    const backBtn = createButton(this, 90, 60, 120, 56, 'Back', PALETTE.panelLight, 20);
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));

    const cols = 3;
    const spacingX = 200;
    const spacingY = 220;
    const startX = INTERNAL_WIDTH / 2 - ((cols - 1) * spacingX) / 2;
    const startY = 250;

    FOODS.forEach((food, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * spacingX;
      const y = startY + row * spacingY;
      this.buildTile(x, y, food);
    });
  }

  private buildTile(x: number, y: number, food: (typeof FOODS)[number]): void {
    const enabled = this.saveManager.isFoodEnabled(food.id);
    const radius = 16;
    const bounds = new Phaser.Geom.Rectangle(x - SWATCH_SIZE / 2, y - SWATCH_SIZE / 2, SWATCH_SIZE, SWATCH_SIZE);

    const bg = this.add.graphics();
    bg.fillStyle(PALETTE.panel, 1);
    bg.fillRoundedRect(bounds.x, bounds.y, SWATCH_SIZE, SWATCH_SIZE, radius);

    const icon = this.add.image(x, y, foodTextureKey(food.id));
    icon.setDisplaySize(SWATCH_SIZE * 0.7, SWATCH_SIZE * 0.7);
    if (!enabled) icon.setAlpha(0.35);

    const border = this.add.graphics();
    border.lineStyle(enabled ? 4 : 2, enabled ? PALETTE.success : 0xffffff, enabled ? 1 : 0.25);
    border.strokeRoundedRect(bounds.x, bounds.y, SWATCH_SIZE, SWATCH_SIZE, radius);

    const badge = this.add.circle(bounds.x + SWATCH_SIZE - 16, bounds.y + 16, 14, enabled ? PALETTE.success : 0x000000, enabled ? 1 : 0.4);
    badge.setStrokeStyle(2, 0xffffff, 0.8);
    this.add
      .text(badge.x, badge.y, enabled ? '✓' : '✕', bodyTextStyle(16, enabled ? '#1a1a1a' : '#ffffff'))
      .setOrigin(0.5);

    this.add.text(x, y + SWATCH_SIZE / 2 + 20, food.name, bodyTextStyle(16)).setOrigin(0.5);

    const hitZone = this.add.zone(x, y, SWATCH_SIZE, SWATCH_SIZE + 40).setInteractive({ useHandCursor: true });
    hitZone.on('pointerup', () => this.onTileTapped(food.id, enabled));
  }

  private onTileTapped(foodId: string, currentlyEnabled: boolean): void {
    const applied = this.saveManager.setFoodEnabled(foodId, !currentlyEnabled);
    if (applied) this.scene.restart();
  }
}
